/**
 * Ingredient detail layer — the "what form, and why that form" content that
 * powers /ingredients/{slug}, /ai/ingredients.json and the agent science API.
 *
 * Source discipline:
 *  - Doses come from the locked TruWe formulation (see mem://features/formulation).
 *  - Research summaries and citations live in `src/lib/research.ts` and are NOT
 *    duplicated here.
 *  - Form-comparison rows are descriptive statements about delivery forms as
 *    reported in published pharmacokinetic literature. No finished-product
 *    performance claims, no invented numbers.
 */

import { RESEARCH, type IngredientResearch } from "@/lib/research";

export type FormComparisonRow = {
  /** The delivery / chemical form being described. */
  form: string;
  /** What published literature and common practice associate with this form. */
  reported: string;
  /** Whether TruWe uses it, and in which product. */
  used: "yes" | "no";
};

export type DoseByProduct = {
  productHandle: string;
  productTitle: string;
  dose: string;
  doseNote?: string;
};

export type IngredientFaq = { q: string; a: string };

export type IngredientDetail = {
  /** One-paragraph, plain-language definition. */
  whatItIs: string;
  /** The exact chemical / branded form TruWe uses. */
  form: string;
  /** Why that form was chosen, grounded in published research. */
  formRationale: string;
  formComparison: FormComparisonRow[];
  doseByProduct: DoseByProduct[];
  faq: IngredientFaq[];
};

const MAG = { handle: "mag-absorb-pro", title: "Mag Absorb Pro" };
const LIVER = { handle: "liver-biome-plus", title: "Liver Biome Plus" };
const OVA = { handle: "ova-balance", title: "Ova Balance" };

function dose(
  p: { handle: string; title: string },
  d: string,
  note?: string,
): DoseByProduct {
  return { productHandle: p.handle, productTitle: p.title, dose: d, ...(note ? { doseNote: note } : {}) };
}

export const INGREDIENT_DETAIL: Record<string, IngredientDetail> = {
  "magnesium-bisglycinate": {
    whatItIs:
      "Magnesium bound to two molecules of the amino acid glycine (a bisglycinate chelate). The chelate keeps the mineral bound as it moves through the gut, which is why this form is the one most commonly studied for sleep, stress and relaxation rather than for laxative effect.",
    form: "Liposomal magnesium bisglycinate (fully reacted chelate, phospholipid-encapsulated)",
    formRationale:
      "Inorganic magnesium salts such as oxide are cheap and high in elemental magnesium by weight, but comparative pharmacokinetic work reports substantially lower absorption for oxide than for the diglycinate chelate. TruWe uses the fully reacted bisglycinate chelate the form used in the sleep and anxiety literature and pairs it with a phospholipid (liposomal) carrier used across the delivery-science literature to protect actives through digestion.",
    formComparison: [
      { form: "Magnesium oxide", reported: "High elemental magnesium per gram, but comparative studies report poor absorption and a strong osmotic/laxative effect.", used: "no" },
      { form: "Magnesium citrate", reported: "Better absorbed than oxide; widely used, but commonly associated with loose stools at higher doses.", used: "no" },
      { form: "Magnesium bisglycinate (diglycinate)", reported: "Amino-acid chelate; comparative bioavailability work reports higher absorption than oxide with better gastrointestinal tolerance. The form used in insomnia and stress trials.", used: "yes" },
      { form: "Liposomal magnesium bisglycinate", reported: "The same chelate carried in a phospholipid matrix, the delivery approach used in published liposomal-nutrient pharmacokinetics.", used: "yes" },
    ],
    doseByProduct: [
      dose(MAG, "200 mg", "elemental magnesium · Phase 3 of the 5-form matrix"),
      dose(OVA, "100 mg", "elemental magnesium · mineral cofactor"),
    ],
    faq: [
      { q: "Why not magnesium oxide, the cheapest form?", a: "Oxide looks strong on the label because it carries a lot of elemental magnesium by weight, but comparative absorption studies report that far less of it actually reaches circulation, and it is the form most associated with a laxative effect." },
      { q: "Is bisglycinate the same as glycinate?", a: "Yes, bisglycinate (diglycinate) means one magnesium ion bound to two glycine molecules. 'Magnesium glycinate' on a label usually refers to the same chelate." },
      { q: "What does liposomal add?", a: "A phospholipid carrier around the active. It is the delivery approach used in published liposomal-nutrient pharmacokinetic studies to protect the payload through digestion." },
      { q: "How much elemental magnesium does Mag Absorb Pro give?", a: "440 mg elemental magnesium across five forms per 2-tablet daily serving; magnesium bisglycinate contributes 200 mg of that." },
      { q: "When should I take it?", a: "Evening is the usual choice, because the sleep and relaxation research on magnesium is built around evening dosing." },
      { q: "Will it upset my stomach?", a: "Chelated forms are generally chosen for tolerability. If you are sensitive, take it with food and start with the standard serving rather than doubling it." },
    ],
  },

  "aquamin-marine-magnesium": {
    whatItIs:
      "A multi-mineral magnesium ingredient extracted from red marine algae (Lithothamnion sp.). Instead of a single isolated salt, it carries magnesium alongside the trace mineral profile the algae accumulates from seawater.",
    form: "Aquamin® marine-derived multi-mineral magnesium (branded, standardised extract)",
    formRationale:
      "Branded, standardised ingredients are traceable to a specific supplier, a specific extraction process and a specific published study set, unlike generic 'marine magnesium'. Aquamin® is the marine mineral ingredient with its own peer-reviewed trial record, which is why it sits in the sustained-release phase of the matrix rather than a commodity salt.",
    formComparison: [
      { form: "Generic 'marine magnesium'", reported: "Unstandardised; source, mineral profile and study record vary by supplier.", used: "no" },
      { form: "Aquamin® (Lithothamnion sp.)", reported: "Standardised branded extract with a published randomised-trial record in joint comfort and bone structure endpoints.", used: "yes" },
      { form: "Synthetic magnesium salt blends", reported: "Predictable elemental content, but no accompanying trace-mineral matrix and no ingredient-specific trial record.", used: "no" },
    ],
    doseByProduct: [dose(MAG, "100 mg", "elemental magnesium · Phase 2 sustained release")],
    faq: [
      { q: "What makes Aquamin® different from ordinary magnesium?", a: "It is a whole mineral extract from red marine algae rather than a single manufactured salt, so it carries magnesium plus the algae's trace-mineral profile, and it has its own published trial record." },
      { q: "Is it vegan?", a: "It is derived from calcified red algae, not from animal sources." },
      { q: "Why include it when the formula already has bisglycinate?", a: "The two behave differently in the gut. The matrix pairs a fast-solubility phase, a sustained phase and a deep-uptake chelate phase rather than relying on any one form." },
      { q: "Is the ® meaningful?", a: "Yes, it identifies the specific standardised ingredient and the study set attached to it, so the research cited actually corresponds to what is in the tablet." },
    ],
  },

  "vitamin-d3": {
    whatItIs: "Vitamin D3, also called cholecalciferol, is a fat-soluble vitamin that supports calcium absorption and normal bone, muscle and immune function.",
    form: "Vitamin D3 (cholecalciferol)",
    formRationale: "Ova Balance supplies cholecalciferol at 600 IU (15 mcg) per sachet. Research on D3 and D2 examines vitamin D status; it does not establish a product-specific absorption percentage or clinical outcomes for Ova Balance.",
    formComparison: [
      { form: "Vitamin D3 (cholecalciferol)", reported: "Studied for its effect on serum 25-hydroxyvitamin D. The cited Trang trial used approximately 4,000 IU daily for 14 days, a different dose from Ova Balance.", used: "yes" },
      { form: "Vitamin D2 (ergocalciferol)", reported: "Another supplemental form of vitamin D, used as the comparator in the cited trial.", used: "no" },
    ],
    doseByProduct: [dose(OVA, "600 IU (15 mcg)", "per sachet; 1 sachet daily")],
    faq: [
      { q: "How much vitamin D3 is in Ova Balance?", a: "Each sachet contains 600 IU (15 mcg) of vitamin D3." },
      { q: "Is 600 IU the same as 15 mcg?", a: "Yes. One microgram of vitamin D equals 40 IU, so 15 mcg equals 600 IU." },
      { q: "Does this research prove Ova Balance improves PCOS or fertility?", a: "No. These sources describe vitamin D and vitamin D status. They are not clinical trials of the finished Ova Balance formula or proof of PCOS or fertility benefits at 600 IU." },
      { q: "Is this vitamin D3 vegan or liposomal?", a: "Those properties depend on the ingredient source and delivery system. Check the final label or contact TruWe for source details; this page makes no vegan or liposomal claim for the vitamin D3." },
    ],
  },

  "myo-inositol": {
    whatItIs:
      "A sugar alcohol the body makes and also gets from food, central to how cells read the insulin signal. In the ovary it is part of the FSH signalling pathway, which is why it is the most-studied nutritional agent in PCOS research.",
    form: "Pharmaceutical-grade myo-inositol powder, dosed in a 40:1 ratio with D-chiro inositol",
    formRationale:
      "The published PCOS literature is built on gram-level myo-inositol doses in the physiological 40:1 myo to D-chiro ratio, the ratio found in healthy plasma. Capsule formats rarely reach that dose without a large pill count, so TruWe delivers it as a single daily sachet at 2000 mg myo-inositol with 50 mg D-chiro inositol.",
    formComparison: [
      { form: "Myo-inositol alone", reported: "Widely studied, but does not supply the D-chiro component of the physiological ratio.", used: "no" },
      { form: "Myo : D-chiro 40:1", reported: "The plasma ratio used across the modern inositol trial literature.", used: "yes" },
      { form: "High D-chiro ratios (e.g. 1:1)", reported: "Literature discusses an ovarian 'D-chiro paradox' at disproportionate D-chiro exposure.", used: "no" },
      { form: "Low-dose inositol capsules", reported: "Convenient, but typically well below the gram-level doses used in published trials.", used: "no" },
    ],
    doseByProduct: [dose(OVA, "2000 mg", "with 50 mg D-chiro inositol · 40:1")],
    faq: [
      { q: "Why a sachet instead of capsules?", a: "The studied dose is measured in grams. Delivering 2050 mg of inositol complex in capsules would mean several large capsules a day." },
      { q: "What does the 40:1 ratio mean?", a: "Forty parts myo-inositol to one part D-chiro inositol, the ratio reported in healthy human plasma and used in the modern trial literature." },
      { q: "How long before anything changes?", a: "Published PCOS trials typically run three to six months. Treat it as a protocol, not a quick fix." },
      { q: "Can I take it with metformin?", a: "Several published studies compare or combine the two, but that is a decision for your doctor, not something to start on your own." },
      { q: "Is inositol a hormone?", a: "No. It is a carbocyclic sugar involved in cell signalling, not a hormone." },
    ],
  },

  "d-chiro-inositol": {
    whatItIs:
      "The second inositol stereoisomer, made in the body from myo-inositol by an insulin-dependent enzyme. It was the subject of the original New England Journal of Medicine PCOS work in 1999.",
    form: "D-chiro inositol, dosed at 50 mg to hold the 40:1 ratio with myo-inositol",
    formRationale:
      "D-chiro inositol matters because of proportion, not quantity. The literature describes reduced ovarian benefit when D-chiro exposure is disproportionately high, so the dose is fixed at 50 mg against 2000 mg myo-inositol to hold the physiological ratio rather than maximised for a bigger number on the label.",
    formComparison: [
      { form: "D-chiro inositol alone", reported: "Studied historically, but modern practice pairs it with myo-inositol.", used: "no" },
      { form: "50 mg D-chiro with 2000 mg myo (40:1)", reported: "Holds the plasma ratio used in current inositol trials.", used: "yes" },
      { form: "High-dose D-chiro", reported: "Literature discusses diminishing ovarian benefit at disproportionate D-chiro exposure.", used: "no" },
    ],
    doseByProduct: [dose(OVA, "50 mg", "40:1 with myo-inositol")],
    faq: [
      { q: "Why so little D-chiro inositol?", a: "Because the ratio is the point. More is not better here, the 40:1 proportion is what the current literature is built on." },
      { q: "Isn't D-chiro the one from the famous PCOS study?", a: "Yes, the 1999 NEJM trial used D-chiro inositol. Later work moved to the combined 40:1 approach." },
      { q: "Do I need to take them separately?", a: "No, both are pre-dosed in a single Ova Balance sachet." },
      { q: "Is it the same as inositol from food?", a: "Food inositol is overwhelmingly myo-inositol. D-chiro is normally produced in the body from it." },
    ],
  },

  "quatrefolic-l-5-mthf": {
    whatItIs:
      "The active, body-ready form of folate, (6S)-5-methyltetrahydrofolate as a glucosamine salt. This is the folate form that actually circulates in blood, as opposed to synthetic folic acid, which the body must convert first.",
    form: "Quatrefolic® (6S)-5-MTHF glucosamine salt, 400 mcg DFE",
    formRationale:
      "Folic acid requires conversion by the MTHFR enzyme before the body can use it, and a large share of the population carries C677T variants that reduce that conversion. 5-MTHF is already in the circulating form, so it does not depend on that step. Quatrefolic® is the branded glucosamine salt chosen for its stability and its published pharmacokinetic record.",
    formComparison: [
      { form: "Folic acid (synthetic)", reported: "Stable and inexpensive; requires enzymatic conversion, which MTHFR variants reduce.", used: "no" },
      { form: "Folinic acid", reported: "An intermediate form; still requires downstream conversion steps.", used: "no" },
      { form: "Calcium salt of 5-MTHF", reported: "Active form; comparatively less soluble than the glucosamine salt.", used: "no" },
      { form: "Quatrefolic® glucosamine salt of (6S)-5-MTHF", reported: "Active circulating form with published comparative pharmacokinetic data against folic acid.", used: "yes" },
    ],
    doseByProduct: [dose(OVA, "400 mcg", "DFE, per sachet")],
    faq: [
      { q: "What is MTHFR and why does it matter?", a: "MTHFR is the enzyme that converts folic acid toward the active form. Common gene variants reduce its efficiency, which is the argument for supplying the active form directly." },
      { q: "Do I need a genetic test first?", a: "No. The active form works regardless of MTHFR status, which is exactly why it was chosen." },
      { q: "Is 400 mcg enough?", a: "400 mcg DFE is the standard daily reference intake for adults. Pregnancy has different requirements, speak to your doctor." },
      { q: "Why the ® on Quatrefolic?", a: "It identifies the specific patented glucosamine salt, so the pharmacokinetic research cited corresponds to the ingredient actually used." },
    ],
  },

  berberine: {
    whatItIs:
      "An isoquinoline alkaloid from plants such as Berberis. It activates AMPK, a cellular energy sensor, which is the mechanism behind its metabolic and glycaemic research record.",
    form: "Berberine hydrochloride (HCl), 300 mg",
    formRationale:
      "Berberine HCl is the salt used across the published metabolic and PCOS trial literature, including the AMPK mechanistic work. TruWe uses the studied salt at the studied dose rather than a novel derivative with a thinner evidence base, and places it only in Ova Balance, where the metabolic rationale applies.",
    formComparison: [
      { form: "Berberine HCl", reported: "The salt used in the published metabolic, glycaemic and PCOS trial literature.", used: "yes" },
      { form: "Berberine sulfate", reported: "Alternative salt; a smaller published human trial record.", used: "no" },
      { form: "Dihydroberberine", reported: "A reduced derivative marketed for absorption; a much smaller clinical record than berberine HCl.", used: "no" },
      { form: "Whole Berberis extract", reported: "Variable alkaloid content unless standardised, so dose cannot be stated precisely.", used: "no" },
    ],
    doseByProduct: [dose(OVA, "300 mg", "berberine HCl")],
    faq: [
      { q: "Is berberine in Liver Biome Plus?", a: "No. Berberine appears only in Ova Balance." },
      { q: "Why HCl and not dihydroberberine?", a: "Because the human trial record including the PCOS and glycaemic meta-analyses is built on berberine HCl." },
      { q: "Can I take it with diabetes medication?", a: "Not without medical supervision. Berberine has documented metabolic activity, so combining it with glucose-lowering medication is a conversation for your doctor." },
      { q: "Does it need to be taken with food?", a: "It is typically taken with a meal, which is how it is dosed in most published protocols." },
    ],
  },

  nac: {
    whatItIs:
      "The acetylated form of the amino acid cysteine. Cysteine availability is the rate-limiting step in making glutathione, the body's primary intracellular antioxidant, which is why NAC is the clinical standard in acetaminophen toxicity.",
    form: "N-acetyl cysteine, 600 mg (standard, non-liposomal)",
    formRationale:
      "NAC is already water-soluble and well absorbed orally, the entire hospital literature uses plain oral or IV NAC. Adding a liposomal carrier to an ingredient that does not need one adds cost without a matching evidence base, so TruWe deliberately uses standard NAC at the 600 mg dose used in the liver literature.",
    formComparison: [
      { form: "N-acetyl cysteine (standard)", reported: "The form used across the oral clinical literature, including NAFLD and antidote protocols.", used: "yes" },
      { form: "Free L-cysteine", reported: "Less stable in supplement form than the acetylated version.", used: "no" },
      { form: "Liposomal NAC", reported: "Marketed for uptake, but the clinical evidence base sits with standard oral NAC.", used: "no" },
      { form: "Glutathione (oral)", reported: "Supplying the end product orally is discussed in the literature as an inefficient route compared with supplying the precursor.", used: "no" },
    ],
    doseByProduct: [dose(LIVER, "600 mg")],
    faq: [
      { q: "Why not just take glutathione directly?", a: "Oral glutathione faces digestion before it can act. Supplying cysteine, the rate-limiting precursor, is the approach the clinical literature is built on." },
      { q: "Why isn't NAC liposomal like the other Liver Biome Plus actives?", a: "Because it does not need to be. NAC is water-soluble and orally well absorbed; liposomal delivery is reserved for the poorly soluble actives in the formula." },
      { q: "Does NAC smell of sulfur?", a: "Sulfur-containing amino acids can have a faint odour. That is characteristic of the ingredient, not a fault." },
      { q: "When is the best time to take it?", a: "It is commonly taken with a meal for comfort; the published protocols are dose-driven rather than timing-driven." },
    ],
  },

  silymarin: {
    whatItIs:
      "A flavonolignan complex from milk thistle seed (Silybum marianum), of which silybin is the main active. It is one of the longest-studied hepatoprotective botanicals in the literature.",
    form: "Liposomal silymarin, 350 mg equivalent",
    formRationale:
      "Silymarin's known limitation is poor aqueous solubility and low oral bioavailability, the reason phospholipid-complexed silybin was developed and studied pharmacokinetically in the first place. TruWe uses a liposomal (phospholipid) carrier for exactly that reason, standardised to a 350 mg silymarin equivalent so the dose is comparable to the literature.",
    formComparison: [
      { form: "Plain milk thistle powder", reported: "Whole-herb material; silymarin content varies and solubility is low.", used: "no" },
      { form: "Standardised silymarin extract", reported: "Defined active content, but the solubility limitation remains.", used: "no" },
      { form: "Phospholipid-complexed / liposomal silymarin", reported: "The delivery approach developed and pharmacokinetically studied to address silymarin's poor solubility.", used: "yes" },
    ],
    doseByProduct: [dose(LIVER, "350 mg Eq", "silymarin equivalent, liposomal")],
    faq: [
      { q: "What does '350 mg Eq' mean?", a: "The dose is expressed as its silymarin equivalent, so it can be compared directly with doses used in published studies rather than to raw herb weight." },
      { q: "Is milk thistle the same as silymarin?", a: "Silymarin is the active flavonolignan complex extracted from milk thistle seed. Plain milk thistle powder is the raw material." },
      { q: "Why liposomal here but not for NAC?", a: "Silymarin is poorly water-soluble, which is the documented reason phospholipid delivery was developed for it. NAC does not have that problem." },
      { q: "Can I take it long term?", a: "Milk thistle has a long use history, but if you have a diagnosed liver condition or take medication, run it past your doctor first." },
    ],
  },

  curcumin: {
    whatItIs:
      "The principal curcuminoid in turmeric root. Extensively studied for its role in the inflammatory response, and equally well known for being difficult to absorb in its plain powdered form.",
    form: "Liposomal curcumin, 100 mg",
    formRationale:
      "Plain curcumin's low oral bioavailability is one of the best-documented problems in nutraceutical pharmacokinetics, the reason a whole literature exists on phospholipid, liposomal and micellar delivery. TruWe uses phospholipid encapsulation rather than raising the raw powder dose, because dose without absorption does not change plasma exposure.",
    formComparison: [
      { form: "Plain curcumin powder", reported: "Very low oral bioavailability is repeatedly documented in pharmacokinetic reviews.", used: "no" },
      { form: "Curcumin + piperine", reported: "Piperine co-administration is studied as a way to slow curcumin metabolism.", used: "no" },
      { form: "Liposomal / phospholipid curcumin", reported: "Encapsulated delivery developed specifically to address curcumin's solubility and absorption limits.", used: "yes" },
      { form: "Micellar curcumin", reported: "Another solubilisation approach with its own pharmacokinetic literature.", used: "no" },
    ],
    doseByProduct: [dose(LIVER, "100 mg", "liposomal")],
    faq: [
      { q: "Why only 100 mg when other brands use 500 mg?", a: "Because raw milligrams of a poorly absorbed powder are not the same as absorbed active. The formula spends its budget on delivery form rather than on a bigger label number." },
      { q: "Is this the same as turmeric?", a: "No. Turmeric is the root; curcumin is the isolated principal curcuminoid within it." },
      { q: "Does it contain black pepper?", a: "Black pepper is part of the Bio-Absorb Matrix blend in the formula; the curcumin itself is phospholipid-encapsulated." },
      { q: "Will it stain or taste strong?", a: "Encapsulated curcumin is contained within the delivery matrix, which is part of why the liposomal form is used." },
    ],
  },

  "alpha-lipoic-acid": {
    whatItIs:
      "A disulfide compound made in mitochondria that is both water- and fat-soluble, the reason it is often called the universal antioxidant. It participates in regenerating other antioxidants including vitamins C and E and glutathione.",
    form: "Liposomal alpha-lipoic acid, 100 mg",
    formRationale:
      "Alpha-lipoic acid is known for rapid clearance and variable oral exposure. A phospholipid carrier is used for the same reason as with the other lipophilic actives in the formula: to protect the payload through digestion, consistent with published liposomal-delivery pharmacokinetics.",
    formComparison: [
      { form: "Racemic alpha-lipoic acid", reported: "The mixed form used in the majority of the published human literature.", used: "yes" },
      { form: "R-lipoic acid (isolated)", reported: "The biologically active isomer; less stable in isolation and a smaller trial record.", used: "no" },
      { form: "Liposomal alpha-lipoic acid", reported: "Encapsulated delivery used to buffer the compound's rapid clearance profile.", used: "yes" },
    ],
    doseByProduct: [dose(LIVER, "100 mg", "liposomal")],
    faq: [
      { q: "What does 'universal antioxidant' mean?", a: "It works in both water-based and fat-based environments in the cell, unlike antioxidants confined to one or the other." },
      { q: "Does it interact with blood sugar?", a: "Alpha-lipoic acid has documented metabolic activity. If you take glucose-lowering medication, check with your doctor." },
      { q: "Why 100 mg?", a: "It sits in a combination formula alongside NAC, silymarin and curcumin, the dose is set for the stack, not as a standalone high-dose ALA product." },
      { q: "Best time to take it?", a: "Commonly taken with the daily Liver Biome Plus serving; no separate timing is required." },
    ],
  },

  probiotics: {
    whatItIs:
      "Live bacterial strains Lactobacillus acidophilus, Bifidobacterium lactis and Lactobacillus rhamnosus included for the gut-liver axis, the two-way traffic between intestinal barrier function and hepatic load.",
    form: "Enteric-protected multi-strain consortium, 10 billion CFU",
    formRationale:
      "Probiotics only matter if they arrive alive. Stomach acid is the documented barrier, which is why the strains are enteric-protected to reach the lower intestine intact, and why CFU count is stated at a defined point rather than left ambiguous. Multi-strain rather than single-strain reflects how the gut-liver literature is structured.",
    formComparison: [
      { form: "Single-strain probiotic", reported: "Simple and well characterised, but narrower than the multi-strain designs used in gut-liver research.", used: "no" },
      { form: "Multi-strain, enteric-protected", reported: "Designed so strains survive gastric transit and reach the lower intestine.", used: "yes" },
      { form: "Unprotected probiotic powder", reported: "Gastric acid exposure is the documented obstacle to viable delivery.", used: "no" },
      { form: "Fermented-food only", reported: "Valuable dietary source, but strain identity and dose are not defined.", used: "no" },
    ],
    doseByProduct: [dose(LIVER, "10 Billion CFU", "L. acidophilus, B. lactis, L. rhamnosus")],
    faq: [
      { q: "What is the gut-liver axis?", a: "The circulatory and immune link between the intestine and the liver. Intestinal barrier function influences what the liver has to process." },
      { q: "Does the tablet need refrigeration?", a: "No. The strains are stabilised and enteric-protected for shelf storage; keep it cool, dry and out of direct sunlight." },
      { q: "Is 10 billion CFU enough?", a: "CFU count matters less than strain identity and survival to the site of action, which is why the strains are named and enteric-protected." },
      { q: "Can I take it with antibiotics?", a: "Space them apart and follow your doctor's advice for your specific course." },
    ],
  },
};

export type IngredientPage = IngredientResearch & {
  detail: IngredientDetail;
  canonicalUrl: string;
};

const BASE = "https://truwe.in";

export function getIngredientPage(slug: string): IngredientPage | null {
  const research = RESEARCH.find((r) => r.slug === slug);
  const detail = INGREDIENT_DETAIL[slug];
  if (!research || !detail) return null;
  return { ...research, detail, canonicalUrl: `${BASE}/ingredients/${slug}` };
}

/** Every ingredient that has a published detail page, alphabetically. */
export function listIngredientPages(): IngredientPage[] {
  return RESEARCH.filter((r) => INGREDIENT_DETAIL[r.slug])
    .map((r) => getIngredientPage(r.slug)!)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const INGREDIENT_SLUGS = Object.keys(INGREDIENT_DETAIL);

/** Delivery-form explainer used on the /ingredients hub. */
export const DELIVERY_FORMS = [
  {
    name: "Liposomal",
    what: "The active is carried inside a phospholipid shell.",
    why: "Used for poorly water-soluble actives curcumin, silymarin, alpha-lipoic acid where published pharmacokinetics identify solubility as the limiting factor.",
  },
  {
    name: "Chelated",
    what: "A mineral bound to an amino acid, such as magnesium bisglycinate.",
    why: "Comparative absorption studies report better uptake and gastrointestinal tolerance than inorganic salts like oxide.",
  },
  {
    name: "Branded & standardised",
    what: "Named ingredients such as Aquamin® and Quatrefolic®.",
    why: "A branded ingredient is traceable to one supplier, one specification and one published study set, so the research cited matches what is in the product.",
  },
  {
    name: "Standard (by design)",
    what: "Plain, unencapsulated actives such as NAC and berberine HCl.",
    why: "Where an active is already well absorbed and the clinical record uses the plain form, adding a carrier would add cost without matching evidence.",
  },
] as const;

/**
 * Resolve a display name used on the /ingredients hub or a PDP to the slug of
 * its detail page. Returns null for blend/proprietary entries with no page.
 */
const NAME_TO_SLUG: Record<string, string> = {
  "liposomal magnesium bisglycinate": "magnesium-bisglycinate",
  "magnesium bisglycinate": "magnesium-bisglycinate",
  "aquamin® marine magnesium": "aquamin-marine-magnesium",
  "vitamin d3": "vitamin-d3",
  "vitamin d3 (cholecalciferol)": "vitamin-d3",
  "cholecalciferol": "vitamin-d3",
  "myo-inositol": "myo-inositol",
  "d-chiro inositol": "d-chiro-inositol",
  "quatrefolic® (l-methylfolate)": "quatrefolic-l-5-mthf",
  "quatrefolic® (l-5-mthf)": "quatrefolic-l-5-mthf",
  "berberine hcl": "berberine",
  "n-acetyl cysteine (nac)": "nac",
  "liposomal milk thistle (silymarin)": "silymarin",
  "liposomal alpha-lipoic acid": "alpha-lipoic-acid",
  "liposomal curcumin": "curcumin",
  "probiotic consortium (l. acidophilus / b. lactis)": "probiotics",
  "probiotic blend (10b cfu)": "probiotics",
};

export function resolveIngredientSlug(name: string): string | null {
  const key = name.trim().toLowerCase();
  const slug = NAME_TO_SLUG[key];
  if (slug && INGREDIENT_DETAIL[slug]) return slug;
  return INGREDIENT_DETAIL[key] ? key : null;
}
